import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EntryComposer from "@/components/journal/EntryComposer";
import ShareDialog from "@/components/ShareDialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Bookmark, Share2, ArrowRight, MoveRight, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function DailySpark() {
  const { user, openAuth, isFavorite, toggleFavorite } = useAuth();
  const [savedEntry, setSavedEntry] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const { data: q, isLoading } = useQuery({
    queryKey: ["daily-page"],
    queryFn: async () => (await api.get("/questions/daily")).data,
  });

  if (isLoading || !q) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-20">
        <div className="h-64 animate-pulse rounded-3xl bg-muted" />
      </div>
    );
  }

  const saved = isFavorite(q.id);
  const onSave = async () => {
    const r = await toggleFavorite(q.id);
    if (r?.needAuth) toast.info("Sign in to save questions.");
    else if (r?.added) toast.success("Saved to your study.");
  };

  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="daily-spark-page">
      <Seo title={q ? `Daily Spark: ${q.question_kjv} (${q.reference})` : "Daily Spark"} description="One question from Scripture every day to carry with you." />
      {/* Hero — today's question */}
      <section className="grain relative overflow-hidden rounded-3xl bg-secondary p-8 text-secondary-foreground sm:p-12">
        <div className="relative z-10">
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-gold">
            <Sparkles className="h-4 w-4" /> Daily Spark · {today}
          </span>
          <Link to={`/question/${q.id}`} data-testid="daily-hero-question">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-serif text-4xl italic leading-[1.05] text-white sm:text-5xl lg:text-6xl"
            >
              &ldquo;{q.question_kjv}&rdquo;
            </motion.p>
          </Link>
          <p className="no-reftag mt-5 flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white/60">
            {q.reference} <span className="text-white/30">·</span> {q.who_asked?.join(", ")}
            <MoveRight className="h-3.5 w-3.5" /> {q.asked_to?.join(", ")}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="daily-save-button"
              onClick={onSave}
              variant={saved ? "default" : "outline"}
              className={`rounded-full ${saved ? "bg-accent text-accent-foreground hover:bg-accent/90" : "border-white/30 bg-transparent text-white hover:bg-white/10"}`}
            >
              <Bookmark className={`mr-2 h-4 w-4 ${saved ? "fill-current" : ""}`} /> {saved ? "Saved" : "Save"}
            </Button>
            <Button
              data-testid="daily-share-button"
              onClick={() => setShareOpen(true)}
              variant="outline"
              className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10"
            >
              <Share2 className="mr-2 h-4 w-4" /> Share
            </Button>
            <Button asChild className="rounded-full bg-white text-secondary hover:bg-white/90">
              <Link to={`/question/${q.id}`} data-testid="daily-explore-button">
                Study it <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Journal */}
      <section className="mt-10" data-testid="daily-compose-section">
        <div className="mb-4 flex items-center gap-2">
          <NotebookPen className="h-5 w-5 text-gold" />
          <h2 className="font-serif text-2xl font-semibold text-foreground">Write it in your journal</h2>
        </div>
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">Sit with today&apos;s question. Your entry is private and linked to this question, so you can find it again. Type @ to link a verse or another question.</p>
        {user ? (
          savedEntry ? (
            <div className="rounded-2xl border border-gold/40 bg-gold/10 p-6" data-testid="daily-entry-saved">
              <p className="font-serif text-xl text-foreground">Saved to your journal.</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <Button asChild className="rounded-full bg-primary text-primary-foreground"><Link to={`/journal/${savedEntry.id}`} data-testid="daily-open-entry">Open entry</Link></Button>
                <Button variant="outline" className="rounded-full" onClick={() => setSavedEntry(null)} data-testid="daily-write-another">Write another</Button>
              </div>
            </div>
          ) : (
            <EntryComposer questionId={q.id} showTitle={false} onSaved={(e) => setSavedEntry(e)} />
          )
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-12 text-center" data-testid="daily-signin-prompt">
            <p className="font-serif text-xl text-foreground">Sign in to keep a journal</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">Your reflections stay private and build up over time.</p>
            <Button data-testid="daily-signin-button" onClick={openAuth} className="mt-5 rounded-full bg-primary text-primary-foreground">
              Sign in to write
            </Button>
          </div>
        )}
      </section>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} question={q} />
    </div>
  );
}
